import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import translations, { languageOrder } from '../i18n/translations';
import { translateError } from '../i18n/errorTranslations';

const LanguageContext = createContext(null);

const DEFAULT_LANG = 'fr';
const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

// Resolve any raw code to a SUPPORTED locale: exact match, then base language (pt-BR->pt), else null.
const resolveSupported = (raw) => {
  if (!raw) return null;
  if (translations[raw]) return raw;
  const base = String(raw).split('-')[0].toLowerCase();
  if (translations[base]) return base;
  return null;
};

// Dev-only missing-key warning (once per key/lang) — replaces the previous SILENT French fallback.
const warned = new Set();
const warnMissingKey = (key, lang) => {
  if (!isDev) return;
  const id = lang + '::' + key;
  if (warned.has(id)) return;
  warned.add(id);
  // eslint-disable-next-line no-console
  console.warn(`[i18n] clé manquante « ${key} » pour la langue « ${lang} » — repli sur le français.`);
};

// {{var}} interpolation (opt-in via t(key, vars)); leaves unknown placeholders untouched.
const interpolate = (str, vars) => {
  if (!vars || typeof str !== 'string') return str;
  return str.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    // 1) Explicit ?lang= deep-link (shareable), validated against supported locales.
    try {
      const q = new URLSearchParams(window.location.search).get('lang');
      const r = resolveSupported(q);
      if (r) { try { localStorage.setItem('app_lang', r); } catch (_) {} return r; }
    } catch (_) { /* no window / bad URL */ }
    // 2) Stored preference.
    try {
      const stored = localStorage.getItem('app_lang');
      const r = resolveSupported(stored);
      if (r) return r;
    } catch (_) {}
    // 3) Browser language.
    const browserLang = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) || '';
    return resolveSupported(browserLang) || DEFAULT_LANG;
  });

  const setLanguage = useCallback((newLang) => {
    const resolved = resolveSupported(newLang) || DEFAULT_LANG;
    setLang(resolved);
    try { localStorage.setItem('app_lang', resolved); } catch (_) {}
  }, []);

  // Drive document direction + lang attribute from the active locale (RTL for ar / ar-SY).
  // The _rtl flag already exists on ar.js / ar-SY.js; here it finally takes effect.
  useEffect(() => {
    try {
      const rtl = !!(translations[lang] && translations[lang]._rtl);
      const el = document.documentElement;
      el.setAttribute('dir', rtl ? 'rtl' : 'ltr');
      el.setAttribute('lang', lang);
    } catch (_) { /* no document */ }
  }, [lang]);

  const t = useCallback((key, vars) => {
    const langData = translations[lang];
    if (langData && langData[key] != null) return interpolate(langData[key], vars);
    // Explicit fallback to French — WARNED in dev, never silent.
    warnMissingKey(key, lang);
    if (translations.fr[key] != null) return interpolate(translations.fr[key], vars);
    return key;
  }, [lang]);

  // Translate backend error messages (base-language fallback handled inside translateError).
  const tError = useCallback((message) => translateError(message, lang), [lang]);

  // Locale-aware formatting helpers — replace hardcoded 'fr-FR' / '€' at call sites.
  const formatNumber = useCallback((n, opts) => {
    try { return new Intl.NumberFormat(lang, opts).format(n); } catch (_) { return String(n); }
  }, [lang]);
  const formatCurrency = useCallback((n, currency = 'EUR', opts) => {
    try { return new Intl.NumberFormat(lang, { style: 'currency', currency, ...(opts || {}) }).format(n); }
    catch (_) { return `${n} ${currency}`; }
  }, [lang]);
  const formatDate = useCallback((d, opts) => {
    try { return new Intl.DateTimeFormat(lang, opts).format(d instanceof Date ? d : new Date(d)); }
    catch (_) { return String(d); }
  }, [lang]);

  const isRtl = !!(translations[lang] && translations[lang]._rtl);

  const languages = languageOrder.map(code => ({
    code,
    name: translations[code]?._name || code,
    flag: translations[code]?._flag || '',
    rtl: !!(translations[code] && translations[code]._rtl),
  }));

  return (
    <LanguageContext.Provider value={{ lang, isRtl, setLanguage, t, tError, formatNumber, formatCurrency, formatDate, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
